import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Snackbar,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  AutoAwesome as GenerateIcon,
  Refresh as RegenerateIcon,
  Save as SaveIcon,
  CheckCircle as FinalIcon,
  History as HistoryIcon,
  ContentCopy as CopyIcon,
  FileDownload as ExportIcon,
  RestartAlt as ResetIcon,
  PlaylistPlay as GenerateAllIcon,
  PlaylistAdd as InitializeIcon,
} from '@mui/icons-material';
import { Document, Packer, Paragraph, HeadingLevel, TextRun, Table, TableRow, TableCell, TableOfContents } from 'docx';
import EditableSectionContent from '../../protocol/components/EditableSectionContent';
import { richTextToBlocks } from '../../utils/richTextToBlocks';
import indService from '../services/ind.service';
import useIndProjectId from '../utils/useIndProjectId';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const byOrder = (a, b) => (a?.order ?? 0) - (b?.order ?? 0);
const hasOutput = (section) => Boolean(String(section?.output || '').trim());

function statusChip(section) {
  if (section?.final) return { label: 'Final', color: 'success' };
  if (hasOutput(section)) return { label: 'Generated', color: 'primary' };
  return { label: 'Draft', color: 'default' };
}

const PROVENANCE_LABEL = { aidrafted: 'AI-drafted', edited: 'Edited', reviewed: 'Edited' };

// --- DOCX export (same approach as SAPEditor.jsx: richTextToBlocks -> docx nodes) ---

function stripLeadingNumber(text) {
  return String(text || '').replace(/^\s*\d+(\.\d+)*\.?\s*/, '').trim();
}
function normalizeHeadingText(text) {
  return stripLeadingNumber(text).toLowerCase().replace(/\s+/g, ' ').trim();
}
function isDuplicateTitleHeading(headingText, sectionTitle) {
  const a = normalizeHeadingText(headingText);
  const b = normalizeHeadingText(sectionTitle);
  return Boolean(a) && a === b;
}

function blocksToDocxChildren(blocks) {
  const children = [];
  if (!Array.isArray(blocks)) return children;
  blocks.forEach((b) => {
    if (b.type === 'heading') {
      const lvl = b.level || 1;
      const headingLevel =
        lvl === 1 ? HeadingLevel.HEADING_1 :
        lvl === 2 ? HeadingLevel.HEADING_2 :
        lvl === 3 ? HeadingLevel.HEADING_3 :
        HeadingLevel.HEADING_4;
      children.push(new Paragraph({
        heading: headingLevel,
        children: [new TextRun({ text: b.text || '', size: 28, bold: true, color: '000000' })],
      }));
    } else if (b.type === 'paragraph') {
      const runs = (b.runs || [{ text: '' }]).map((r) =>
        new TextRun({ text: r.text || '', bold: !!r.bold, italics: !!r.italic, size: 24, color: '000000' })
      );
      children.push(new Paragraph({ children: runs }));
    } else if (b.type === 'list') {
      (b.items || []).forEach((runs) => {
        const text = (runs || []).map((r) => r.text || '').join('');
        children.push(new Paragraph({ text, bullet: { level: 0 } }));
      });
    } else if (b.type === 'table') {
      // Rows with zero cells are invalid OOXML (Word reports the file as corrupt).
      const rows = (b.rows || [])
        .filter((row) => Array.isArray(row) && row.length > 0)
        .map((row) => new TableRow({
          children: row.map((cellRuns) => new TableCell({
            ...(cellRuns?.rowSpan > 1 ? { rowSpan: cellRuns.rowSpan } : {}),
            ...(cellRuns?.colSpan > 1 ? { columnSpan: cellRuns.colSpan } : {}),
            children: [new Paragraph({
              children: (cellRuns || []).map((r) =>
                new TextRun({ text: r.text || '', bold: !!r.bold, italics: !!r.italic, size: 24, color: '000000' })
              ),
            })],
          })),
        }));
      if (rows.length > 0) children.push(new Table({ rows }));
    }
  });
  return children;
}

function buildDocx(docTitle, sections) {
  const children = [
    new Paragraph({ heading: HeadingLevel.TITLE, children: [new TextRun({ text: docTitle, bold: true })] }),
    new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: 'Table of Contents', size: 28, bold: true })] }),
    new TableOfContents('', { hyperlink: true, headingStyleRange: '1-4' }),
  ];
  sections.forEach((section) => {
    children.push(new Paragraph({ children: [], pageBreakBefore: true }));
    children.push(new Paragraph({ text: section.title, heading: HeadingLevel.HEADING_1 }));
    const raw = String(section.output || '').trim();
    if (raw) {
      const blocks = richTextToBlocks(raw);
      // Generated content starts with an <h2> equal to the title; drop it so the
      // heading above is not printed twice.
      if (blocks[0]?.type === 'heading' && isDuplicateTitleHeading(blocks[0].text, section.title)) {
        blocks.shift();
      }
      children.push(...blocksToDocxChildren(blocks));
    } else {
      children.push(new Paragraph({ text: 'No content generated' }));
    }
  });
  return new Document({ features: { updateFields: true }, sections: [{ properties: {}, children }] });
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

const jobProgressText = (job) => {
  if (!job) return '';
  const steps = Array.isArray(job.steps) ? job.steps : [];
  const last = steps.length ? steps[steps.length - 1] : null;
  return last?.message || last?.name || job.message || (job.status ? `Job ${job.status}` : '');
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const INDDocumentEditor = ({ documentKey, catalogueDocument, projectId: projectIdProp }) => {
  const navigate = useNavigate();
  const { projectId: resolvedProjectId } = useIndProjectId();
  const projectId = projectIdProp || resolvedProjectId;

  const [catalogueDoc, setCatalogueDoc] = useState(catalogueDocument || null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [draftHtml, setDraftHtml] = useState('');
  const [draftDirty, setDraftDirty] = useState(false);
  const [resetCounter, setResetCounter] = useState(0);
  const [generating, setGenerating] = useState({});
  const [progress, setProgress] = useState({});
  const [generatingAll, setGeneratingAll] = useState(false);
  const [busy, setBusy] = useState(null); // 'init' | 'save' | 'final' | 'export' | 'reset'
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenText, setRegenText] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  const mountedRef = useRef(true);
  const selectedIdRef = useRef(null);
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Catalogue document: use the prop when given, otherwise fetch it once.
  useEffect(() => {
    if (catalogueDocument) {
      setCatalogueDoc(catalogueDocument);
      return;
    }
    let cancelled = false;
    indService.getCatalogue()
      .then((data) => {
        if (cancelled) return;
        const doc = (data?.documents || []).find((d) => d.key === documentKey) || null;
        setCatalogueDoc(doc);
      })
      .catch((err) => {
        console.error('INDDocumentEditor: failed to load catalogue', err);
      });
    return () => { cancelled = true; };
  }, [catalogueDocument, documentKey]);

  const loadSections = useCallback(async () => {
    if (!projectId || !documentKey) return [];
    setLoading(true);
    setError(null);
    try {
      const list = await indService.getAllSections(projectId, documentKey);
      const sorted = [...(list || [])].filter((s) => !s.documentKey || s.documentKey === documentKey).sort(byOrder);
      if (!mountedRef.current) return sorted;
      setSections(sorted);
      setSelectedId((prev) => (prev && sorted.some((s) => s._id === prev) ? prev : sorted[0]?._id || null));
      return sorted;
    } catch (err) {
      console.error('INDDocumentEditor: failed to load sections', err);
      if (mountedRef.current) setError(err.message || 'Failed to load IND sections');
      return [];
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [projectId, documentKey]);

  useEffect(() => {
    loadSections();
  }, [loadSections]);

  const selectedSection = sections.find((s) => s._id === selectedId) || null;
  const catalogueSection = catalogueDoc?.sections?.find((s) => s.key === selectedSection?.sectionKey) || null;

  // Reset the editable draft whenever a different section is selected.
  useEffect(() => {
    selectedIdRef.current = selectedId;
    const current = sectionsRef.current.find((s) => s._id === selectedId);
    setDraftHtml(current?.output || '');
    setDraftDirty(false);
    setResetCounter((c) => c + 1);
  }, [selectedId]);

  const applySectionUpdate = (updated) => {
    if (!updated?._id) return;
    setSections((prev) => prev.map((s) => (s._id === updated._id ? { ...s, ...updated } : s)).sort(byOrder));
  };

  const notify = (severity, message) => setNotice({ severity, message });

  // --- actions -------------------------------------------------------------

  const handleInitialize = async () => {
    setBusy('init');
    setError(null);
    try {
      const result = await indService.initializeDocument(projectId, documentKey);
      await loadSections();
      notify('success', `Initialized ${result?.created ?? 0} section${result?.created === 1 ? '' : 's'}.`);
    } catch (err) {
      console.error('INDDocumentEditor: initialize failed', err);
      setError(err.message || 'Failed to initialize sections');
    } finally {
      setBusy(null);
    }
  };

  const runGenerate = async (section, userInstructions) => {
    const id = section._id;
    setGenerating((prev) => ({ ...prev, [id]: true }));
    setProgress((prev) => ({ ...prev, [id]: 'Starting generation...' }));
    try {
      const output = await indService.generateIndSectionViaJob(id, projectId, userInstructions, (job) => {
        if (!mountedRef.current) return;
        setProgress((prev) => ({ ...prev, [id]: jobProgressText(job) }));
      });
      let fresh = null;
      try {
        fresh = await indService.getSection(id, projectId);
      } catch (refetchErr) {
        console.warn('INDDocumentEditor: could not refetch section after generation', refetchErr?.message || refetchErr);
      }
      if (!mountedRef.current) return true;
      applySectionUpdate(fresh?._id ? fresh : { _id: id, output, provenance: 'aidrafted' });
      if (selectedIdRef.current === id) {
        setDraftHtml(fresh?.output || output || '');
        setDraftDirty(false);
        setResetCounter((c) => c + 1);
      }
      return true;
    } catch (err) {
      console.error(`INDDocumentEditor: generation failed for "${section.title}"`, err);
      if (mountedRef.current) notify('error', `Generation failed for "${section.title}": ${err.message || 'unknown error'}`);
      return false;
    } finally {
      if (mountedRef.current) {
        setGenerating((prev) => { const next = { ...prev }; delete next[id]; return next; });
        setProgress((prev) => { const next = { ...prev }; delete next[id]; return next; });
      }
    }
  };

  const handleGenerate = async () => {
    if (!selectedSection) return;
    const ok = await runGenerate(selectedSection);
    if (ok) notify('success', `Generated "${selectedSection.title}".`);
  };

  const handleRegenerateConfirm = async () => {
    if (!selectedSection) return;
    const instructions = regenText.trim();
    setRegenOpen(false);
    const ok = await runGenerate(selectedSection, instructions || undefined);
    if (ok) {
      setRegenText('');
      notify('success', `Regenerated "${selectedSection.title}".`);
    }
  };

  const handleGenerateAll = async () => {
    const queue = [...sections].sort(byOrder).filter((s) => !s.final);
    if (queue.length === 0) {
      notify('info', 'Nothing to generate: every section is final.');
      return;
    }
    setGeneratingAll(true);
    const failures = [];
    for (const section of queue) {
      if (!mountedRef.current) break;
      // eslint-disable-next-line no-await-in-loop
      const ok = await runGenerate(section);
      if (!ok) failures.push(section.title);
    }
    if (!mountedRef.current) return;
    setGeneratingAll(false);
    if (failures.length === 0) {
      notify('success', `Generated ${queue.length} section${queue.length === 1 ? '' : 's'}.`);
    } else {
      notify('warning', `Generated ${queue.length - failures.length} of ${queue.length}; failed: ${failures.join('; ')}`);
    }
  };

  const saveDraft = async () => {
    if (!selectedSection) return false;
    setBusy('save');
    try {
      const updated = await indService.updateSection(
        selectedSection._id,
        { output: draftHtml, provenance: 'edited' },
        projectId
      );
      applySectionUpdate(updated?._id ? updated : { _id: selectedSection._id, output: draftHtml, provenance: 'edited' });
      setDraftDirty(false);
      return true;
    } catch (err) {
      console.error('INDDocumentEditor: save failed', err);
      notify('error', err.message || 'Failed to save section');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const handleSave = async () => {
    if (await saveDraft()) notify('success', 'Section saved.');
  };

  const handleMarkFinal = async () => {
    if (!selectedSection) return;
    if (draftDirty && !(await saveDraft())) return;
    setBusy('final');
    try {
      const updated = await indService.updateSection(selectedSection._id, { final: true }, projectId);
      applySectionUpdate(updated?._id ? updated : { _id: selectedSection._id, final: true });
      notify('success', `"${selectedSection.title}" marked final.`);
    } catch (err) {
      console.error('INDDocumentEditor: mark final failed', err);
      notify('error', err.message || 'Failed to mark section final');
    } finally {
      setBusy(null);
    }
  };

  const handleVersionHistory = () => {
    if (!selectedSection) return;
    navigate(`/ind-section-history/${selectedSection._id}?projectId=${projectId}`);
  };

  const handleCopyHtml = async () => {
    const html = draftHtml || selectedSection?.output || '';
    try {
      await navigator.clipboard.writeText(html);
      notify('success', 'Section HTML copied to clipboard.');
    } catch (err) {
      console.error('INDDocumentEditor: clipboard write failed', err);
      notify('error', 'Could not copy to clipboard.');
    }
  };

  const handleExportDocx = async () => {
    setBusy('export');
    try {
      const title = catalogueDoc?.title || documentKey;
      const doc = buildDocx(title, [...sections].sort(byOrder));
      const blob = await Packer.toBlob(doc);
      downloadBlob(blob, `ind-${documentKey}.docx`);
    } catch (err) {
      console.error('INDDocumentEditor: DOCX export failed', err);
      notify('error', 'Failed to export document to Word format.');
    } finally {
      setBusy(null);
    }
  };

  const handleResetConfirm = async () => {
    setResetOpen(false);
    setBusy('reset');
    setError(null);
    try {
      await indService.deleteAllSections(projectId, documentKey);
      await indService.initializeDocument(projectId, documentKey);
      setSelectedId(null);
      await loadSections();
      notify('success', 'Document reset: sections re-created from the catalogue.');
    } catch (err) {
      console.error('INDDocumentEditor: reset failed', err);
      setError(err.message || 'Failed to reset document');
    } finally {
      setBusy(null);
    }
  };

  // --- render --------------------------------------------------------------

  if (!projectId) {
    return <Alert severity="info">Select a study to author its IND documents.</Alert>;
  }

  const anyGenerating = generatingAll || Object.keys(generating).length > 0;
  const selectedGenerating = Boolean(selectedSection && generating[selectedSection._id]);
  const actionsLocked = Boolean(busy) || anyGenerating;
  const finalCount = sections.filter((s) => s.final).length;
  const generatedCount = sections.filter(hasOutput).length;

  return (
    <Box>
      {/* Document toolbar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6">{catalogueDoc?.title || documentKey}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
              {catalogueDoc?.ctdLocation && <Chip size="small" variant="outlined" label={`CTD ${catalogueDoc.ctdLocation}`} />}
              {(catalogueDoc?.basis || []).map((b) => <Chip key={b} size="small" variant="outlined" label={b} />)}
              {sections.length > 0 && (
                <Chip size="small" label={`${generatedCount}/${sections.length} generated, ${finalCount} final`} />
              )}
            </Stack>
            {catalogueDoc?.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{catalogueDoc.description}</Typography>
            )}
          </Box>
          {sections.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="small"
                startIcon={generatingAll ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <GenerateAllIcon />}
                disabled={actionsLocked}
                onClick={handleGenerateAll}
              >
                Generate all
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={busy === 'export' ? <CircularProgress size={16} /> : <ExportIcon />}
                disabled={Boolean(busy)}
                onClick={handleExportDocx}
              >
                Export DOCX
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={busy === 'reset' ? <CircularProgress size={16} /> : <ResetIcon />}
                disabled={actionsLocked}
                onClick={() => setResetOpen(true)}
              >
                Reset document
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : sections.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>No sections yet for this document.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Initializing creates every catalogue section for {catalogueDoc?.shortTitle || 'this document'} so you can generate them one by one or all at once.
          </Typography>
          <Button
            variant="contained"
            startIcon={busy === 'init' ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <InitializeIcon />}
            disabled={Boolean(busy)}
            onClick={handleInitialize}
          >
            Initialize sections
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Section list */}
          <Paper sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0, maxHeight: { md: '75vh' }, overflowY: 'auto' }}>
            <List dense disablePadding>
              {sections.map((section) => {
                const chip = statusChip(section);
                const provenance = PROVENANCE_LABEL[section.provenance];
                return (
                  <ListItemButton
                    key={section._id}
                    selected={section._id === selectedId}
                    onClick={() => setSelectedId(section._id)}
                    divider
                    sx={{ alignItems: 'flex-start' }}
                  >
                    <ListItemText
                      primary={section.title}
                      primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: section._id === selectedId ? 600 : 500 } }}
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                          <Chip size="small" color={chip.color} variant={chip.color === 'default' ? 'outlined' : 'filled'} label={chip.label} />
                          {provenance && <Chip size="small" variant="outlined" label={provenance} />}
                          {generating[section._id] && <Chip size="small" color="info" icon={<CircularProgress size={12} />} label="Generating" />}
                        </Stack>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>

          {/* Selected section */}
          <Paper sx={{ flex: 1, minWidth: 0, p: 2 }}>
            {!selectedSection ? (
              <Typography color="text.secondary">Select a section to view or edit it.</Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6">{selectedSection.title}</Typography>
                    {catalogueSection?.guidance && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{catalogueSection.guidance}</Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Chip size="small" {...statusChip(selectedSection)} />
                    {draftDirty && <Chip size="small" color="warning" label="Unsaved" />}
                  </Stack>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={selectedGenerating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <GenerateIcon />}
                    disabled={actionsLocked || selectedSection.final}
                    onClick={handleGenerate}
                  >
                    {hasOutput(selectedSection) ? 'Regenerate' : 'Generate'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RegenerateIcon />}
                    disabled={actionsLocked || selectedSection.final}
                    onClick={() => setRegenOpen(true)}
                  >
                    Regenerate with instructions
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    startIcon={busy === 'save' ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />}
                    disabled={actionsLocked || !draftDirty}
                    onClick={handleSave}
                  >
                    Save
                  </Button>
                  <Button
                    variant={selectedSection.final ? 'outlined' : 'contained'}
                    color="success"
                    size="small"
                    startIcon={busy === 'final' ? <CircularProgress size={16} /> : <FinalIcon />}
                    disabled={actionsLocked || selectedSection.final || !hasOutput(selectedSection)}
                    onClick={handleMarkFinal}
                  >
                    {selectedSection.final ? 'Final' : 'Mark final'}
                  </Button>
                  <Tooltip title="Open the version history for this section">
                    <span>
                      <Button variant="text" size="small" startIcon={<HistoryIcon />} onClick={handleVersionHistory}>
                        Version history
                      </Button>
                    </span>
                  </Tooltip>
                  <Button variant="text" size="small" startIcon={<CopyIcon />} onClick={handleCopyHtml} disabled={!hasOutput(selectedSection) && !draftHtml}>
                    Copy HTML
                  </Button>
                </Box>

                {selectedGenerating && (
                  <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
                    {progress[selectedSection._id] || 'Generating...'}
                  </Alert>
                )}
                {selectedSection.final && (
                  <Alert severity="success" sx={{ mb: 2 }}>This section is final. Generation is disabled; edits can still be saved.</Alert>
                )}

                <EditableSectionContent
                  html={selectedSection.output || ''}
                  resetKey={`${selectedSection._id}:${resetCounter}`}
                  placeholder="No content yet. Generate this section or start typing."
                  onChange={(html) => {
                    setDraftHtml(html);
                    setDraftDirty(true);
                  }}
                  sx={{ opacity: selectedGenerating ? 0.6 : 1, pointerEvents: selectedGenerating ? 'none' : 'auto' }}
                />
              </>
            )}
          </Paper>
        </Box>
      )}

      {/* Regenerate with instructions */}
      <Dialog open={regenOpen} onClose={() => setRegenOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Regenerate with instructions</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The current content of &quot;{selectedSection?.title}&quot; is sent to the model together with your instructions.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            label="Edit instructions"
            placeholder="e.g. Shorten the toxicology summary and add the NOAEL for the dog study in a table."
            value={regenText}
            onChange={(e) => setRegenText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRegenerateConfirm} disabled={!regenText.trim()}>Regenerate</Button>
        </DialogActions>
      </Dialog>

      {/* Reset confirmation */}
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)}>
        <DialogTitle>Reset this document?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            All {sections.length} sections of &quot;{catalogueDoc?.title || documentKey}&quot;, including their content and version history, are deleted and re-created empty from the catalogue. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleResetConfirm}>Reset document</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={notice?.severity === 'error' || notice?.severity === 'warning' ? 10000 : 4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={notice?.severity || 'info'} onClose={() => setNotice(null)} variant="filled" sx={{ maxWidth: 640 }}>
          {notice?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

INDDocumentEditor.propTypes = {
  documentKey: PropTypes.string.isRequired,
  catalogueDocument: PropTypes.object,
  projectId: PropTypes.string,
};

export default INDDocumentEditor;
